"use client";

import { useState } from "react";
import { usePrivy, useWallets } from "@privy-io/react-auth";

type WithdrawButtonProps = {
  contractType?: "REVENUE_DISTRIBUTOR" | "AGREEMENT";
  label?: string;
};

export default function WithdrawButton({
  contractType,
  label = "Withdraw Share-In-Revenue",
}: WithdrawButtonProps) {
  const { getAccessToken } = usePrivy();
  const { wallets } = useWallets();
  const [withdrawing, setWithdrawing] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleWithdraw = async () => {
    try {
      setWithdrawing(true);
      setStatus(null);
      setError(null);

      const wallet = wallets[0];
      const provider = await wallet?.getEthereumProvider();
      if (!wallet || !provider) throw new Error("Your Privy wallet is unavailable.");

      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error("Sign in again before withdrawing.");

      const API_BASE_URL =
        process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";

      const res = await fetch(
        `${API_BASE_URL}/api/customer/${wallet.address}/withdraw`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Withdrawal execution failed.");
      }

      if (
        typeof data.walletAddress !== "string" ||
        data.walletAddress.toLowerCase() !== wallet.address.toLowerCase()
      ) {
        throw new Error("The connected wallet does not match the wallet authorized for this withdrawal.");
      }

      if (contractType && data.contractType !== contractType) {
        throw new Error("The backend resolved a different contract than this page expects.");
      }

      const hash = await provider.request({
        method: "eth_sendTransaction",
        params: [{ from: wallet.address, to: data.transaction.to, data: data.transaction.data }],
      });

      const targetText =
        data.contractType === "REVENUE_DISTRIBUTOR"
          ? "RevenueDistributor Smart Contract"
          : "AGPSIRAAgreement Smart Contract";

      setStatus(
        `Withdrawal submitted from ${targetText}. Tx Hash: ${String(hash)}`
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Withdrawal request failed. Ensure your wallet is connected."
      );
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <div className="w-full space-y-3">
      <button
        type="button"
        onClick={handleWithdraw}
        disabled={withdrawing}
        className="w-full rounded-2xl bg-emerald-600 px-5 py-4 font-semibold text-white shadow-lg shadow-emerald-600/30 transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {withdrawing ? "Executing Withdrawal..." : label}
      </button>

      {status && (
        <div className="rounded-xl border border-emerald-500/50 bg-emerald-500/10 p-3.5 text-xs text-emerald-200">
          {status}
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-500/50 bg-red-500/10 p-3.5 text-xs text-red-200">
          {error}
        </div>
      )}
    </div>
  );
}
